import { issuedQuestion, subscribeIssuedQuestions } from './issued-questions';
import { speechPlayer } from '../playback/player';
import { voiceConfigStore, voiceMode } from '../state';
import type { SpeechPlayer, VoiceConfigStore, VoiceModeState, VoiceRef } from '../contracts';
import { ACTION_TURN_CHANGED } from '@/components/assistant-ui/action-presentation';

type Outcome = { id: string; digest: string; status: 'executed' | 'failed' };
type PreparedOutcome = { settle(result: Outcome): void; cancel(): void };

/** Prepares one card-tap outcome from server-issued question metadata. */
export function createActionOutcomeSpeaker({
  config = voiceConfigStore,
  mode = voiceMode,
  player = speechPlayer,
}: {
  config?: Pick<VoiceConfigStore, 'current' | 'subscribe'>;
  mode?: VoiceModeState;
  player?: Pick<SpeechPlayer, 'speak'>;
} = {}) {
  let prepared: PreparedOutcome | null = null;
  const cancel = () => {
    prepared?.cancel();
    prepared = null;
  };
  return {
    prepare(ref: VoiceRef, digest: string, threadId: string): PreparedOutcome | null {
      cancel();
      const question = issuedQuestion(ref);
      const current = config.current();
      const voice = current.voice;
      const readAloud = current.readAloud;
      if (
        ref.kind !== 'action' ||
        !threadId ||
        !question?.messageId ||
        question.digest !== digest ||
        !mode.active() ||
        voice?.policy.readAloud !== true ||
        voice.prefs.readAloud !== true ||
        voice.prefs.speakWith !== 'poneva' ||
        readAloud?.available !== true
      )
        return null;
      const sourceMessageId = question.messageId;
      let aborted = false;
      let settled = false;
      let abort: AbortController | null = null;
      let unsubscribeMode: (() => void) | null = null;
      let unsubscribeConfig: (() => void) | null = null;
      let unsubscribeQuestions: (() => void) | null = null;
      const onTurn = (event: Event) => {
        const detail = (event as CustomEvent<{ threadId: string; running: boolean }>).detail;
        if (detail?.threadId === threadId && detail.running) operation.cancel();
      };
      const cleanup = () => {
        window.removeEventListener(ACTION_TURN_CHANGED, onTurn);
        unsubscribeMode?.();
        unsubscribeConfig?.();
        unsubscribeQuestions?.();
        unsubscribeMode = null;
        unsubscribeConfig = null;
        unsubscribeQuestions = null;
      };
      const operation: PreparedOutcome = {
        settle(result) {
          if (aborted || settled) return;
          if (
            result.id !== ref.id ||
            result.digest !== digest ||
            (result.status !== 'executed' && result.status !== 'failed')
          ) {
            operation.cancel();
            return;
          }
          settled = true;
          abort = new AbortController();
          const line = result.status === 'executed' ? 'executed' : 'failed';
          void player
            .speak(
              { kind: 'confirmation', ref, digest, messageId: sourceMessageId, line },
              question.lines[line],
              { rate: voice.prefs.rate },
              abort.signal,
            )
            .catch(() => {})
            .finally(cleanup);
        },
        cancel() {
          if (aborted) return;
          aborted = true;
          abort?.abort();
          cleanup();
        },
      };
      prepared = operation;
      const invalidate = () => operation.cancel();
      unsubscribeMode = mode.subscribe(invalidate);
      unsubscribeConfig = config.subscribe(invalidate);
      unsubscribeQuestions = subscribeIssuedQuestions(invalidate);
      window.addEventListener(ACTION_TURN_CHANGED, onTurn);
      return operation;
    },
    cancel,
    dispose() {
      cancel();
    },
  };
}
